import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Lock, Flag, MountainSnow, Footprints, Users } from "lucide-react";
import { ZONES, zoneIndexForPage } from "../lib/reader";
import { ContourSwatch } from "./TrailProfile";

// The whole journey as stops on the roadmap toward the pinnacle. Pure navigation.
export const TrailMap = ({ open, onClose, currentPage, onJump, unlocked, freeLimit = 24, note, pauses = [], socialWaypoints = [] }) => {
  const here = zoneIndexForPage(currentPage);
  const stops = [
    { id: "intro", name: "Trailhead", full: "The Approach", blurb: "Where the walking begins.", start_page: 1, gap: 4 },
    ...ZONES,
  ];
  const restCount = pauses.length;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[100] overflow-y-auto bg-ivory-100"
          data-testid="trail-map"
        >
          <div className="mx-auto max-w-3xl px-6 py-10 sm:px-10 sm:py-14">
            <div className="flex items-start justify-between">
              <div>
                <p className="kicker text-gold-foil">The Trail Map</p>
                <h2 className="mt-2 font-display text-3xl text-ink sm:text-4xl">Every stop on the way to the pinnacle</h2>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-ink-soft">
                  A map of the whole journey — chapters, waypoints and rest points. You can see the full
                  climb even before every door is open. Tap any stop to go there.
                </p>
              </div>
              <button type="button" onClick={onClose} aria-label="Close" data-testid="trail-map-close"
                className="debossed-btn flex h-10 w-10 flex-none items-center justify-center rounded-full text-ink-soft">
                <X strokeWidth={1.5} className="h-5 w-5" />
              </button>
            </div>

            {note && (
              <div className="mt-7 rounded-2xl bg-ivory-50 p-5 ring-1 ring-gold/25" data-testid="trail-map-note">
                <p className="inline-flex items-center gap-2 font-display text-sm text-gold-deep">
                  <Users strokeWidth={1.5} className="h-4 w-4" /> Walk this with someone
                </p>
                <p className="mt-2 text-sm leading-relaxed text-ink-soft">{note}</p>
              </div>
            )}

            <div className="relative mt-9 pl-7">
              <div className="absolute bottom-2 left-[10px] top-2 w-px bg-gradient-to-b from-gold/40 via-ivory-400/70 to-gold/50" />
              {stops.map((z, i) => {
                const locked = !unlocked && z.start_page > freeLimit;
                const isHere = i === here + 1 || (here < 0 && i === 0);
                return (
                  <div key={z.id} className="relative mb-6 last:mb-0" data-testid={`trail-stop-${z.id}`}>
                    <span className={`absolute -left-[22px] top-3 flex h-3.5 w-3.5 items-center justify-center rounded-full ring-2 ring-ivory-100 ${isHere ? "bg-gold-deep" : locked ? "bg-ivory-400" : "bg-gold/70"}`} />
                    <button
                      type="button"
                      disabled={locked}
                      onClick={() => { if (!locked) { onJump(z.start_page); onClose(); } }}
                      data-testid={`trail-stop-btn-${z.id}`}
                      className={`group flex w-full items-center gap-4 rounded-2xl p-4 text-left ring-1 transition ${
                        locked ? "cursor-not-allowed bg-ivory-50/60 ring-ivory-400/50"
                        : isHere ? "bg-gold/10 ring-gold/40 hover:ring-gold/60"
                        : "bg-ivory-50 ring-ivory-400/60 hover:-translate-y-0.5 hover:ring-gold/40"}`}
                    >
                      <ContourSwatch gap={z.gap} size={46} className={`flex-none ${isHere ? "text-gold-deep" : "text-ink-soft"}`} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] uppercase tracking-editorial text-ink-muted">
                          {z.full || z.name}{isHere ? " · you are here" : ""}
                        </p>
                        <p className="font-display text-lg text-ink">{z.name}</p>
                        <p className="truncate text-sm text-ink-soft">{z.blurb}</p>
                      </div>
                      {locked
                        ? <Lock strokeWidth={1.6} className="h-4 w-4 flex-none text-ink-muted" />
                        : <span className="flex-none text-xs text-ink-muted">p.{z.start_page}</span>}
                    </button>
                  </div>
                );
              })}
              <div className="relative mb-0">
                <span className="absolute -left-[22px] top-3 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-gold ring-2 ring-ivory-100" />
                <div className="flex items-center gap-4 rounded-2xl bg-gradient-to-r from-gold/15 to-ivory-50 p-4 ring-1 ring-gold/30">
                  <MountainSnow strokeWidth={1.5} className="h-6 w-6 flex-none text-gold-deep" />
                  <div>
                    <p className="text-[11px] uppercase tracking-editorial text-gold-deep">The Pinnacle</p>
                    <p className="font-display text-lg text-ink">Arriving — rhythm, not endurance</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-ink-muted">
              <span className="inline-flex items-center gap-1.5"><Flag strokeWidth={1.5} className="h-3.5 w-3.5 text-gold-deep" /> Waypoint at each zone — a gentle pause to notice what moved</span>
              {restCount > 0 && (
                <span className="inline-flex items-center gap-1.5"><Footprints strokeWidth={1.5} className="h-3.5 w-3.5 text-gold-deep" /> {restCount} rest points along the way</span>
              )}
            </div>

            {socialWaypoints.length > 0 && (
              <div className="mt-8 rounded-2xl bg-ivory-50 p-5 ring-1 ring-ivory-400/60" data-testid="trail-map-social">
                <p className="inline-flex items-center gap-2 font-display text-sm text-gold-deep">
                  <Users strokeWidth={1.5} className="h-4 w-4" /> Your Social Circle — reflections
                </p>
                <ul className="mt-3 space-y-2">
                  {socialWaypoints.map((w) => (
                    <li key={w.id} className="text-sm leading-relaxed text-ink-soft">— {w.prompt}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-10 text-center">
              <button type="button" onClick={onClose} data-testid="trail-map-continue"
                className="debossed-btn rounded-full px-8 py-3 font-display text-lg text-ink">
                Continue reading
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
